/** @jsx jsx */
import { jsx } from '@emotion/core'
import { FacebookShareButton, FacebookIcon } from "react-share";
import { FacebookMessengerShareButton, FacebookMessengerIcon } from "react-share";
import { LinkedinShareButton, LinkedinIcon } from "react-share";
import { TwitterShareButton, TwitterIcon } from "react-share";
import { WhatsappShareButton, WhatsappIcon } from "react-share";
import { EmailShareButton, EmailIcon } from "react-share";
import { PinterestShareButton, PinterestIcon } from "react-share";
import { TelegramShareButton, TelegramIcon } from "react-share";
import { RedditShareButton, RedditIcon } from "react-share";
import { TumblrShareButton, TumblrIcon } from "react-share";
import { VKShareButton, VKIcon } from "react-share";
import { PocketShareButton, PocketIcon } from "react-share";

import { getShareButtonStyle } from './shareButtonStyle';

export function ShareButtons({ 
  shareUrl, 
  pageTitle = null, 
  topLabel = 'SHARE THIS PAGE:',
  selectedPlatforms = ['facebook', 'whatsapp', 'email'],
  buttonPosition = 'center'
}) {
  const s = getShareButtonStyle();
  const title = pageTitle || (typeof window !== 'undefined' ? window.document.title : '');
  
  const allPlatforms = [
    // Most popular platforms (react-share supported)
    { key: 'facebook', name: 'Facebook', component: FacebookShareButton, icon: FacebookIcon, isCustom: false },
    { key: 'facebook-messenger', name: 'Facebook Messenger', component: FacebookMessengerShareButton, icon: FacebookMessengerIcon, isCustom: false },
    { key: 'x-twitter', name: 'X (Twitter)', component: TwitterShareButton, icon: TwitterIcon, isCustom: false },
    { key: 'whatsapp', name: 'WhatsApp', component: WhatsappShareButton, icon: WhatsappIcon, isCustom: false },
    { key: 'telegram', name: 'Telegram', component: TelegramShareButton, icon: TelegramIcon, isCustom: false },
    { key: 'linkedin', name: 'LinkedIn', component: LinkedinShareButton, icon: LinkedinIcon, isCustom: false },
    { key: 'pinterest', name: 'Pinterest', component: PinterestShareButton, icon: PinterestIcon, isCustom: false },
    { key: 'email', name: 'Email', component: EmailShareButton, icon: EmailIcon, isCustom: false },
    
    // Regional and specialized platforms (react-share supported)
    { key: 'vk', name: 'VK', component: VKShareButton, icon: VKIcon, isCustom: false },
    { key: 'reddit', name: 'Reddit', component: RedditShareButton, icon: RedditIcon, isCustom: false },
    { key: 'tumblr', name: 'Tumblr', component: TumblrShareButton, icon: TumblrIcon, isCustom: false },
    { key: 'pocket', name: 'Pocket', component: PocketShareButton, icon: PocketIcon, isCustom: false },
    
    // Custom implementations for platforms not in react-share
    { key: 'odnoklassniki', name: 'Odnoklassniki', component: null, icon: null, isCustom: true, url: 'https://connect.ok.ru/offer?url={url}&title={title}' },
    { key: 'mail-ru', name: 'Mail.Ru', component: null, icon: null, isCustom: true, url: 'https://connect.mail.ru/share?url={url}&title={title}' },
    { key: 'livejournal', name: 'LiveJournal', component: null, icon: null, isCustom: true, url: 'https://www.livejournal.com/update.bml?event={url}&subject={title}' },
    { key: 'viber', name: 'Viber', component: null, icon: null, isCustom: true, url: 'viber://forward?text={title} {url}' },
    { key: 'workplace', name: 'Workplace', component: null, icon: null, isCustom: true, url: 'https://work.facebook.com/sharer/sharer.php?u={url}' },
    { key: 'line', name: 'Line', component: null, icon: null, isCustom: true, url: 'https://social-plugins.line.me/lineit/share?url={url}' },
    { key: 'weibo', name: 'Weibo', component: null, icon: null, isCustom: true, url: 'https://service.weibo.com/share/share.php?url={url}&title={title}' },
    { key: 'instapaper', name: 'Instapaper', component: null, icon: null, isCustom: true, url: 'http://www.instapaper.com/edit?url={url}&title={title}' },
    { key: 'hatena', name: 'Hatena', component: null, icon: null, isCustom: true, url: 'http://b.hatena.ne.jp/bookmarklet?url={url}&btitle={title}' },
    { key: 'gab', name: 'Gab', component: null, icon: null, isCustom: true, url: 'https://gab.com/compose?url={url}&text={title}' },
    { key: 'bluesky', name: 'Bluesky', component: null, icon: null, isCustom: true, url: 'https://bsky.app/intent/compose?text={title} {url}' },
    { key: 'threads', name: 'Threads', component: null, icon: null, isCustom: true, url: 'https://www.threads.net/intent/post?text={title} {url}' },
    
    // Custom options
    { key: 'instagram', name: 'Instagram', component: null, icon: null, isCustom: true, url: 'https://www.instagram.com/', isSpecial: true },
    { key: 'copy-link', name: 'Copy Link', component: null, icon: null, isCustom: true, url: '', isSpecial: true }
  ];

  const renderShareButton = (platform) => {
    const { key, name, component: ShareButton, icon: Icon, isCustom, url: customUrl, isSpecial } = platform;
    const isEnabled = selectedPlatforms.includes(key);
    
    if (!isEnabled) return null;

    // Handle special platforms (Instagram and Copy Link)
    if (isSpecial) {
      if (key === 'instagram') {
        const handleInstagramShare = () => {
          const shareText = `${title}\n\n${shareUrl}`;
          
          // Create a better user experience with a modal
          const modal = document.createElement('div');
          modal.style.cssText = `
            position: fixed;
            top: 0;
            left: 0;
            width: 100%;
            height: 100%;
            background: rgba(0,0,0,0.8);
            z-index: 10000;
            display: flex;
            align-items: center;
            justify-content: center;
            font-family: Arial, sans-serif;
          `;
          
          modal.innerHTML = `
            <div style="
              background: white;
              padding: 30px;
              border-radius: 10px;
              max-width: 500px;
              width: 90%;
              text-align: center;
              box-shadow: 0 10px 30px rgba(0,0,0,0.3);
              position: relative;
            ">
              <button onclick="this.closest('.instagram-modal').remove()" style="
                position: absolute;
                top: 15px;
                right: 15px;
                background: none;
                border: none;
                font-size: 24px;
                cursor: pointer;
                color: #999;
                width: 30px;
                height: 30px;
                display: flex;
                align-items: center;
                justify-content: center;
                border-radius: 50%;
                transition: background-color 0.2s;
              " onmouseover="this.style.backgroundColor='#f0f0f0'" onmouseout="this.style.backgroundColor='transparent'">×</button>
              <div style="
                width: 60px;
                height: 60px;
                background: linear-gradient(45deg, #f09433 0%, #e6683c 25%, #dc2743 50%, #cc2366 75%, #bc1888 100%);
                border-radius: 15px;
                margin: 0 auto 20px auto;
                display: flex;
                align-items: center;
                justify-content: center;
                box-shadow: 0 4px 15px rgba(225, 48, 108, 0.3);
              ">
                <svg width="32" height="32" viewBox="0 0 24 24" fill="white">
                  <path d="M12 2.163c3.204 0 3.584.012 4.85.07 3.252.148 4.771 1.691 4.919 4.919.058 1.265.069 1.645.069 4.849 0 3.205-.012 3.584-.069 4.849-.149 3.225-1.664 4.771-4.919 4.919-1.266.058-1.644.07-4.85.07-3.204 0-3.584-.012-4.849-.07-3.26-.149-4.771-1.699-4.919-4.92-.058-1.265-.07-1.644-.07-4.849 0-3.204.013-3.583.07-4.849.149-3.227 1.664-4.771 4.919-4.919 1.266-.057 1.645-.069 4.849-.069zm0-2.163c-3.259 0-3.667.014-4.947.072-4.358.2-6.78 2.618-6.98 6.98-.059 1.281-.073 1.689-.073 4.948 0 3.259.014 3.668.072 4.948.2 4.358 2.618 6.78 6.98 6.98 1.281.058 1.689.072 4.948.072 3.259 0 3.668-.014 4.948-.072 4.354-.2 6.782-2.618 6.979-6.98.059-1.28.073-1.689.073-4.948 0-3.259-.014-3.667-.072-4.947-.196-4.354-2.617-6.78-6.979-6.98-1.281-.059-1.69-.073-4.949-.073zm0 5.838c-3.403 0-6.162 2.759-6.162 6.162s2.759 6.163 6.162 6.163 6.162-2.759 6.162-6.163c0-3.403-2.759-6.162-6.162-6.162zm0 10.162c-2.209 0-4-1.79-4-4 0-2.209 1.791-4 4-4s4 1.791 4 4c0 2.21-1.791 4-4 4zm6.406-11.845c-.796 0-1.441.645-1.441 1.44s.645 1.44 1.441 1.44c.795 0 1.439-.645 1.439-1.44s-.644-1.44-1.439-1.44z"/>
                </svg>
              </div>
              <h3 style="color: #E4405F; margin: 0 0 20px 0; font-size: 24px;">Share on Instagram</h3>
              <p style="margin: 0 0 20px 0; color: #666; line-height: 1.5;">
                Content has been copied to your clipboard!<br>
                Follow these steps to share on Instagram:
              </p>
              <div style="
                background: #f8f9fa;
                padding: 15px;
                border-radius: 8px;
                margin: 20px 0;
                text-align: left;
                font-family: monospace;
                font-size: 14px;
                white-space: pre-wrap;
                border-left: 4px solid #E4405F;
              ">${shareText}</div>
              <ol style="text-align: left; margin: 20px 0; padding-left: 20px;">
                <li>Open Instagram app on your phone</li>
                <li>Tap the + button to create a new post</li>
                <li>Add your photo/video</li>
                <li>Tap "Write a caption..." and paste the content</li>
                <li>Share your post!</li>
              </ol>
              <div style="margin-top: 25px;">
                <button onclick="this.closest('.instagram-modal').remove()" style="
                  background: #E4405F;
                  color: white;
                  border: none;
                  padding: 12px 24px;
                  border-radius: 6px;
                  cursor: pointer;
                  font-size: 16px;
                  margin-right: 10px;
                ">Got it!</button>
                <button onclick="
                  navigator.clipboard.writeText('${shareText.replace(/'/g, "\\'")}');
                  this.textContent = 'Copied again!';
                  setTimeout(() => this.textContent = 'Copy again', 2000);
                " style="
                  background: #6c757d;
                  color: white;
                  border: none;
                  padding: 12px 24px;
                  border-radius: 6px;
                  cursor: pointer;
                  font-size: 16px;
                ">Copy again</button>
              </div>
            </div>
          `;
          
          modal.className = 'instagram-modal';
          document.body.appendChild(modal);
          
          // Copy to clipboard
          navigator.clipboard.writeText(shareText).catch(() => {
            // Fallback if clipboard fails
            const textArea = document.createElement('textarea');
            textArea.value = shareText;
            document.body.appendChild(textArea);
            textArea.select();
            document.execCommand('copy');
            document.body.removeChild(textArea);
          });
        };
        
        return (
          <span key={key} title="Copy content for Instagram sharing">
            <button 
              onClick={handleInstagramShare}
              css={s.shareButton}
              style={{ 
                display: 'inline-block',
                width: '50px',
                height: '50px',
                borderRadius: '50%',
                background: 'linear-gradient(45deg, #f09433 0%, #e6683c 25%, #dc2743 50%, #cc2366 75%, #bc1888 100%)',
                color: 'white',
                textDecoration: 'none',
                textAlign: 'center',
                lineHeight: '50px',
                fontSize: '20px',
                fontWeight: 'bold',
                border: 'none',
                cursor: 'pointer',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                transition: 'all 0.2s ease',
                boxShadow: '0 2px 8px rgba(225, 48, 108, 0.3)'
              }}
              onMouseOver={(e) => {
                e.target.style.transform = 'scale(1.05)'
                e.target.style.boxShadow = '0 4px 12px rgba(225, 48, 108, 0.4)'
              }}
              onMouseOut={(e) => {
                e.target.style.transform = 'scale(1)'
                e.target.style.boxShadow = '0 2px 8px rgba(225, 48, 108, 0.3)'
              }}
            >
              <svg width="24" height="24" viewBox="0 0 24 24" fill="white">
                <path d="M12 2.163c3.204 0 3.584.012 4.85.07 3.252.148 4.771 1.691 4.919 4.919.058 1.265.069 1.645.069 4.849 0 3.205-.012 3.584-.069 4.849-.149 3.225-1.664 4.771-4.919 4.919-1.266.058-1.644.07-4.85.07-3.204 0-3.584-.012-4.849-.07-3.26-.149-4.771-1.699-4.919-4.92-.058-1.265-.07-1.644-.07-4.849 0-3.204.013-3.583.07-4.849.149-3.227 1.664-4.771 4.919-4.919 1.266-.057 1.645-.069 4.849-.069zm0-2.163c-3.259 0-3.667.014-4.947.072-4.358.2-6.78 2.618-6.98 6.98-.059 1.281-.073 1.689-.073 4.948 0 3.259.014 3.668.072 4.948.2 4.358 2.618 6.78 6.98 6.98 1.281.058 1.689.072 4.948.072 3.259 0 3.668-.014 4.948-.072 4.354-.2 6.782-2.618 6.979-6.98.059-1.28.073-1.689.073-4.948 0-3.259-.014-3.667-.072-4.947-.196-4.354-2.617-6.78-6.979-6.98-1.281-.059-1.69-.073-4.949-.073zm0 5.838c-3.403 0-6.162 2.759-6.162 6.162s2.759 6.163 6.162 6.163 6.162-2.759 6.162-6.163c0-3.403-2.759-6.162-6.162-6.162zm0 10.162c-2.209 0-4-1.79-4-4 0-2.209 1.791-4 4-4s4 1.791 4 4c0 2.21-1.791 4-4 4zm6.406-11.845c-.796 0-1.441.645-1.441 1.44s.645 1.44 1.441 1.44c.795 0 1.439-.645 1.439-1.44s-.644-1.44-1.439-1.44z"/>
              </svg>
            </button>
          </span>
        );
      }
      
      if (key === 'copy-link') {
        const handleCopyLink = async () => {
          try {
            await navigator.clipboard.writeText(shareUrl);
            // You could add a toast notification here
            alert('Link copied to clipboard!');
          } catch (err) {
            // Fallback for older browsers
            const textArea = document.createElement('textarea');
            textArea.value = shareUrl;
            document.body.appendChild(textArea);
            textArea.select();
            document.execCommand('copy');
            document.body.removeChild(textArea);
            alert('Link copied to clipboard!');
          }
        };
        
        return (
          <span key={key} title="Copy link to clipboard">
            <button 
              onClick={handleCopyLink}
              css={s.shareButton}
              style={{ 
                display: 'inline-block',
                width: '50px',
                height: '50px',
                borderRadius: '50%',
                backgroundColor: '#28a745', // Green color for copy action
                color: 'white',
                textDecoration: 'none',
                textAlign: 'center',
                lineHeight: '50px',
                fontSize: '16px',
                fontWeight: 'bold',
                border: 'none',
                cursor: 'pointer',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                transition: 'all 0.2s ease',
                boxShadow: '0 2px 8px rgba(40, 167, 69, 0.3)'
              }}
              onMouseOver={(e) => {
                e.target.style.backgroundColor = '#218838'
                e.target.style.transform = 'scale(1.05)'
              }}
              onMouseOut={(e) => {
                e.target.style.backgroundColor = '#28a745'
                e.target.style.transform = 'scale(1)'
              }}
            >
              <svg width="20" height="20" viewBox="0 0 24 24" fill="white">
                <path d="M16 1H4c-1.1 0-2 .9-2 2v14h2V3h12V1zm3 4H8c-1.1 0-2 .9-2 2v14c0 1.1.9 2 2 2h11c1.1 0 2-.9 2-2V7c0-1.1-.9-2-2-2zm0 16H8V7h11v14z"/>
              </svg>
            </button>
    </span>
        );
      }
    }

    // Handle custom platforms (not in react-share)
    if (isCustom) {
      const encodedUrl = encodeURIComponent(shareUrl);
      const encodedTitle = encodeURIComponent(title);
      const finalUrl = customUrl.replace('{url}', encodedUrl).replace('{title}', encodedTitle);
      
      return (
        <span key={key} title={`Sharing with ${name}`}>
          <a 
            href={finalUrl} 
            target="_blank" 
            rel="noopener noreferrer"
            css={s.shareButton}
            style={{ 
              display: 'inline-block',
              width: '50px',
              height: '50px',
              borderRadius: '50%',
              backgroundColor: '#4267B2',
              color: 'white',
              textDecoration: 'none',
              textAlign: 'center',
              lineHeight: '50px',
              fontSize: '12px',
              fontWeight: 'bold',
              transition: 'all 0.2s ease',
              boxShadow: '0 2px 8px rgba(66, 103, 178, 0.3)'
            }}
            onMouseOver={(e) => {
              e.target.style.transform = 'scale(1.05)'
              e.target.style.boxShadow = '0 4px 12px rgba(66, 103, 178, 0.4)'
            }}
            onMouseOut={(e) => {
              e.target.style.transform = 'scale(1)'
              e.target.style.boxShadow = '0 2px 8px rgba(66, 103, 178, 0.3)'
            }}
          >
            {name.charAt(0)}
          </a>
    </span>
      );
    }

    // Handle react-share platforms
    const shareProps = {
      url: shareUrl,
      css: [s.shareButton, {
        transition: 'all 0.2s ease',
        '&:hover': {
          transform: 'scale(1.05)',
          boxShadow: '0 4px 12px rgba(0,0,0,0.15)'
        }
      }]
    };

    // Add platform-specific props
    if (key === 'x-twitter' || key === 'linkedin') {
      shareProps.title = title;
    } else if (key === 'email') {
      shareProps.subject = title;
    } else if (key === 'pinterest') {
      shareProps.media = shareUrl; // You might want to pass a specific image URL
    }

    return (
      <span key={key} title={`Sharing with ${name}`}>
        <ShareButton {...shareProps}>
          <Icon size={50} round={true} />
        </ShareButton>
    </span>
    );
  };

  const getButtonPositionStyle = () => {
    switch (buttonPosition) {
      case 'left':
        return { 
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'flex-start'
        }
      case 'right':
        return { 
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'flex-end'
        }
      case 'center':
      default:
        return { 
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center'
        }
    }
  }

  return (
    <div css={[s.shareButtons, getButtonPositionStyle()]}>
      {topLabel && <div css={s.shareButtonTitle}>{topLabel}</div>}
      <div css={s.shareButtonsContainer}>
        {allPlatforms.map(renderShareButton)}
      </div>
  </div>
  );
}
